import { createColumnHelper } from '@tanstack/react-table';
import React from 'react';
import { Table, SearchActions, Button, Badge, ConfirmDialog, unwrapTrpcData } from '@org/ui';
import { trpc, RouterOutputs } from '@org/clients/school';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { getMutationErrorMessage } from '../shared/multi-step-utils';
import { PendingRequestsDialog } from './pending-requests-dialog';
import { usePendingRequests } from './pending-requests-panel';
import { sortProductsBaseFirst } from './product-form-rules';

type ProductRow = RouterOutputs['products']['list'][number];

type ProductsListProps = {
  onCreateProduct: () => void;
  onEditProduct: (product: ProductRow) => void;
};

export function formatPrice(priceCents: number, currency: string, locale: string): string {
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(priceCents / 100);
  } catch {
    // Unknown currency code: fall back to a plain number so the row still renders.
    return `${currency} ${(priceCents / 100).toFixed(2)}`;
  }
}

export const ProductsList: React.FC<ProductsListProps> = ({ onCreateProduct, onEditProduct }) => {
  const { t, i18n } = useTranslation('admin');
  const utils = trpc.useUtils();
  const { data } = trpc.products.list.useQuery();
  const [filterValue, setFilterValue] = React.useState("");
  const [pendingDelete, setPendingDelete] = React.useState<ProductRow | null>(null);
  const [productData, setProductData] = React.useState<ProductRow[]>([]);
  const [requestsOpen, setRequestsOpen] = React.useState(false);
  const { rows: pendingRequests } = usePendingRequests();
  const pendingCount = pendingRequests.length;

  React.useEffect(() => {
    // The base product (the store's main thing) always comes first.
    setProductData(sortProductsBaseFirst(unwrapTrpcData<ProductRow[]>(data as ProductRow[]) ?? []));
  }, [data]);

  const deleteMutation = trpc.products.delete.useMutation({
    onSuccess: async () => {
      await utils.products.list.invalidate();
      toast.success(t('products.list.toasts.deleteSuccess'));
    },
    onError: (error: unknown) =>
      toast.error(t('products.list.toasts.deleteError', { message: getMutationErrorMessage(error) })),
  });

  const handleDelete = (product: ProductRow) => {
    setPendingDelete(product);
  };

  const confirmDelete = () => {
    if (!pendingDelete) return;
    deleteMutation.mutate({ productId: pendingDelete.productId });
    setPendingDelete(null);
  };

  const handleFilterChange = (value: string) => {
    setFilterValue(value);
  };

  const columnHelper = createColumnHelper<ProductRow>();

  const columns = [
    columnHelper.display({
      id: 'cover',
      header: t('products.list.table.columns.cover'),
      cell: (info) => {
        const row = info.row.original;
        return row.coverImageUrl ? (
          <img
            src={row.coverImageUrl}
            alt=""
            className="h-14 w-10 rounded-sm border border-border object-cover"
          />
        ) : (
          <div className="flex h-14 w-10 items-center justify-center rounded-sm border border-dashed border-border text-[10px] uppercase text-muted-foreground">
            {t('products.list.noCover')}
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row.name || t('products.list.noName'), {
      id: 'name',
      header: t('products.list.table.columns.name'),
      cell: (info) => {
        const row = info.row.original;
        return (
          <div className="flex flex-col">
            <span className="flex items-center gap-2">
              <span className="font-medium">{info.getValue()}</span>
              {row.kind === 'base' && (
                <Badge className="bg-rose-600 text-white hover:bg-rose-600">{t('products.list.baseBadge')}</Badge>
              )}
            </span>
            {row.tagline ? (
              <span className="text-xs text-muted-foreground">{row.tagline}</span>
            ) : null}
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => (row.kind === 'base' ? t('products.list.basePath') : row.journeyName || ''), {
      id: 'journey',
      header: t('products.list.table.columns.journey'),
    }),
    columnHelper.accessor((row) => row.journeyLevelName || '', {
      id: 'level',
      header: t('products.list.table.columns.level'),
    }),
    columnHelper.accessor(
      (row) =>
        row.defaultDurationDays == null
          ? t('products.list.neverExpires')
          : t('products.list.durationDays', { count: row.defaultDurationDays }),
      {
        id: 'duration',
        header: t('products.list.table.columns.duration'),
      },
    ),
    columnHelper.accessor(
      (row) =>
        row.priceCents === 0
          ? t('products.list.free')
          : formatPrice(row.priceCents, row.currency, i18n.language),
      {
        id: 'price',
        header: t('products.list.table.columns.price'),
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="flex flex-col">
              <span>{info.getValue()}</span>
              {row.billing === 'monthly' && (
                <span className="text-xs text-muted-foreground">{t('products.list.billingMonthly')}</span>
              )}
              {row.kind === 'base' && row.trialDays ? (
                <span className="text-xs text-muted-foreground">
                  {t('products.list.trialDays', { count: row.trialDays })}
                </span>
              ) : null}
            </div>
          );
        },
      },
    ),
    columnHelper.accessor((row) => (row.isActive ? t('products.list.activeYes') : t('products.list.activeNo')), {
      id: 'active',
      header: t('products.list.table.columns.active'),
      cell: (info) => (
        <Badge variant={info.row.original.isActive ? 'outline' : 'secondary'}>{info.getValue()}</Badge>
      ),
    }),
    columnHelper.display({
      id: 'edit',
      header: t('products.list.table.columns.actions'),
      cell: (info) => {
        const row = info.row.original;
        return (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onEditProduct(row)}
            >
              {t('products.list.actions.edit')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-red-600"
              disabled={deleteMutation.isPending}
              onClick={(event) => {
                event.stopPropagation();
                handleDelete(row);
              }}
            >
              {t('products.list.actions.delete')}
            </Button>
          </div>
        );
      },
    }),
  ];

  return (
    <div>
      <div><SearchActions
          onClickAdd={onCreateProduct}
          onFilterChange={handleFilterChange}
          extraActions={
            <Button
              variant={pendingCount > 0 ? 'default' : 'outline'}
              onClick={() => setRequestsOpen(true)}
            >
              {t('pendingRequests.headerButton', { count: pendingCount })}
            </Button>
          }
        />
        </div>
      <div><Table
        tableData={productData as any}
        columns={columns}
        getRowId={(row: any) => row.productId}
        globalFilter={filterValue}
      /></div>
      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
        description={t('products.list.deleteConfirm', { name: pendingDelete?.name ?? '' })}
        confirmLabel={t('products.list.actions.delete')}
        destructive
        onConfirm={confirmDelete}
      />
      <PendingRequestsDialog open={requestsOpen} onOpenChange={setRequestsOpen} />
    </div>
  )
}
